// Auto Metadata Generation
// Automatically generate title and tags for notes using LLM
//
// IMPORTANT: This module does NOT directly depend on platform.
// API key and chat function must be provided via options parameter.

/**
 * Generate title and tags for a note using LLM
 * @param {string} content - Note content
 * @param {Object} options - Generation options
 * @param {Function} [options.chat] - Chat function from LLM SDK instance
 * @param {string} [options.provider='gemini'] - LLM provider
 * @param {string} [options.model='gemini-2.5-flash'] - Model name
 * @param {string} [options.apiKey] - API key (required if provider needs it)
 * @returns {Promise<Object>} { title, tags }
 */
const DEFAULT_MODELS = {
  gemini: 'gemini-2.5-flash',
  openai: 'gpt-4o-mini',
}

export const generateMetadata = async (content, options = {}) => {
  const {
    chat,
    provider = 'gemini',
    model = DEFAULT_MODELS[provider] || 'gemini-2.5-flash',
    apiKey
  } = options

  try {
    const prompt = `请为以下用户保存的信息生成一个简短的标题（最多 10 个字）和 2-5 个标签。

内容：
${content}

返回 JSON 格式：
{
  "title": "简短的标题",
  "tags": ["标签1", "标签2", "标签3"]
}

只返回 JSON，不要包含其他文字。`

    if (!chat || !apiKey) {
      console.warn(
        `[AutoMetadata] Missing chat function or API key for provider ${provider}, using fallback`
      )
      return fallbackMetadata(content)
    }

    const response = await chat({
      provider,
      apiKey,
      model,
      messages: [{ role: 'user', content: prompt }],
    })

    // Parse LLM response
    const responseContent = response.message.content.trim()

    // Try to extract JSON from the response
    let metadata
    const jsonMatch = responseContent.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      metadata = JSON.parse(jsonMatch[0])
    } else {
      metadata = JSON.parse(responseContent)
    }

    // Validate and return
    const title = metadata.title || fallbackTitle(content)
    const tags = Array.isArray(metadata.tags) ? metadata.tags : []

    console.log(`[AutoMetadata] Generated title: "${title}", tags: [${tags.join(', ')}]`)

    return {
      title,
      tags,
    }
  } catch (error) {
    console.error('[AutoMetadata] Failed:', error.message)
    return fallbackMetadata(content)
  }
}

/**
 * Fallback metadata generation when LLM is unavailable
 * @param {string} content - Note content
 * @returns {Object} { title, tags }
 */
const fallbackMetadata = (content) => {
  console.log('[AutoMetadata] Using fallback metadata generation')
  return {
    title: fallbackTitle(content),
    tags: [],
  }
}

/**
 * Generate a simple title from content
 * @param {string} content - Note content
 * @returns {string} Title
 */
const fallbackTitle = (content) => {
  // Use first line or first 20 characters
  const firstLine = content.split('\n')[0]
  if (firstLine.length <= 20) {
    return firstLine
  }
  return content.substring(0, 20) + '...'
}

export default generateMetadata
